/**
 * Material credit values — DERIVED FROM RECIPES, NOT AUTHORED (M3 S13).
 *
 * What a unit of material is worth, in credits, so that systems which need to
 * compare a common against a rare have one honest answer instead of each
 * inventing its own.
 *
 * THE WHOLE POINT IS THAT NOBODY WRITES THESE NUMBERS DOWN. A hand-authored
 * rarity price table drifts the moment a recipe changes: retune what an
 * uncommon costs to craft and the table keeps quoting the old economy, silently,
 * forever. The same argument demand-core makes for region mixes (§6, "DERIVED,
 * NOT AUTHORED") applies here with more force, because this one prices a sink.
 *
 * The derivation is the game's own crafting ladder:
 *
 *     value(rarity) = craftingCost(rarity) + Σ value(each input)
 *
 * with the base of the recursion being a rarity nothing crafts — commons drop,
 * they are not made, so a common is worth exactly what the ladder charges to
 * work with it. Everything above is that plus what it consumed.
 *
 * Against the shipped data that resolves to:
 *
 *     common    5      (craftingCost, the unit of account)
 *     uncommon  30     = 3 commons + 15
 *     rare      115    = 3 commons + 1 uncommon + 70
 *
 * — a 1 : 6 : 23 ladder. Worth knowing WHY that is exact rather than an average:
 * all 18 uncommon recipes take exactly 3 commons and all 12 rare recipes take
 * exactly 3 commons and 1 uncommon. Thirty recipes, two shapes. If that ever
 * stops being true this file keeps working — it walks the actual recipes — but
 * the per-rarity summary above stops being a fair description of it.
 *
 * No browser dependencies — the simulator runs this too.
 */

import * as config from '../constants.js';
import { findItem } from './util.js';

/**
 * The crafting ladder gives the RATIOS between rarities; this sets the scale.
 *
 * ⚠ IT EXISTS TO MAKE CONVOY PAYOUTS PLAUSIBLE, and it is the only number here
 * that is a judgement rather than a derivation. Unscaled, a common was worth 5
 * and a Long Haul's 340-credit manifest fetched 12,390 — a region paying 36x
 * what the goods are worth, and up to 135x at the extreme. That reads as
 * nonsense however the numbers were arrived at.
 *
 * A single scalar cannot flatten the SPREAD (8.7x to 135x across classes,
 * routes and demand) because that spread comes from the flat `credits` term and
 * the 2.4x route range — but it moves the whole band, and the window is narrow:
 *
 *     K <= 8.7   keeps the worst case at or above 1x, so a convoy is never paid
 *                LESS than its cargo is worth
 *     K >= 6.75  brings the best case under 20x
 *
 * 8 sits inside it, giving 1.1x at worst and 16.9x at best. The high end is a
 * cheap manifest hauled the full width of the map into a starving market, which
 * ought to be lucrative; the low end is the biggest class on the shortest hop,
 * which the class comment already calls a poor trade.
 *
 * NOTHING MECHANICAL DEPENDS ON THE SCALE, only on ratios — but two constants
 * must move WITH it or they silently change meaning: `OPERATION_TEMPLATES
 * .cargoValue` (or manifests buy 1/K the goods) and `REGION_APPETITE` (or
 * markets saturate K times faster). `materialValue.test.js` pins the payout
 * band so a future retune of any of the three cannot drift them apart.
 */
export const MATERIAL_VALUE_SCALE = 8;

/** Every recipe in the game, whatever tier it crafts. */
function allRecipes() {
  return [
    ...(config.recipesUncommon || []),
    ...(config.recipesRare || []),
    ...(config.recipesEpic || []),
    ...(config.recipesArtifact || []),
  ];
}

// Pure function of static config, and walked recursively — compute once.
let valueCache = null;

/**
 * Credit value per unit, keyed by material id.
 *
 * Built by walking each recipe from its target back through its inputs.
 * Memoised per id with an in-progress guard, so a cyclic recipe graph (which
 * would otherwise recurse forever) resolves to the bare crafting cost rather
 * than hanging the game.
 */
function allValues() {
  if (valueCache) return valueCache;

  const recipeFor = new Map();
  for (const recipe of allRecipes()) {
    if (recipe?.target && !recipeFor.has(recipe.target)) recipeFor.set(recipe.target, recipe);
  }

  const values = new Map();
  const resolving = new Set();

  const costOf = (rarity) => config.itemRarities?.[rarity]?.craftingCost || 0;

  const valueOf = (itemId) => {
    if (values.has(itemId)) return values.get(itemId);
    const item = findItem('id', itemId);
    const base = costOf(item?.rarity);
    // Guard BEFORE recursing. A recipe graph is a tree today; a cycle
    // introduced later should degrade to "worth its crafting cost" rather than
    // blow the stack on load.
    if (resolving.has(itemId)) return base;

    const recipe = recipeFor.get(itemId);
    if (!recipe) {
      values.set(itemId, base);      // a drop, not a craft — the base of the ladder
      return base;
    }

    resolving.add(itemId);
    let total = base;
    for (const input of recipe.inputs || []) {
      total += valueOf(input.id) * (input.qty || 0);
    }
    resolving.delete(itemId);

    values.set(itemId, total);
    return total;
  };

  for (const pool of ['itemsCommon', 'itemsUncommon', 'itemsRare']) {
    for (const item of config[pool] || []) valueOf(item.id);
  }
  for (const recipe of allRecipes()) if (recipe?.target) valueOf(recipe.target);

  valueCache = values;
  return valueCache;
}

/**
 * Credit value of one unit of a material.
 *
 * Falls back to the rarity's crafting cost for anything with no recipe and no
 * pool entry, and to 0 for an id the game does not know — a caller summing an
 * unknown id should get "worth nothing", never NaN, because these values divide
 * budgets.
 */
export function materialValue(itemId) {
  if (!itemId) return 0;
  // Scaled at the PUBLIC boundary, not inside the recursion — `allValues()`
  // walks recipes and would compound the factor once per tier if it were
  // applied there, making a rare 8x too dear relative to a common and breaking
  // the very ratios the ladder exists to give.
  const cached = allValues().get(itemId);
  if (cached != null) return cached * MATERIAL_VALUE_SCALE;
  const item = findItem('id', itemId);
  return (config.itemRarities?.[item?.rarity]?.craftingCost || 0) * MATERIAL_VALUE_SCALE;
}

/** Credit value of a manifest — `{itemId: qty}` — summed at per-unit value. */
export function manifestValue(manifest) {
  let total = 0;
  for (const [itemId, qty] of Object.entries(manifest || {})) {
    total += materialValue(itemId) * (qty || 0);
  }
  return total;
}

/** The per-rarity ladder, for display and for tests that assert its shape. */
export function rarityValues() {
  const out = {};
  for (const [pool, rarity] of [['itemsCommon', 'common'], ['itemsUncommon', 'uncommon'], ['itemsRare', 'rare']]) {
    const first = (config[pool] || [])[0];
    if (first) out[rarity] = materialValue(first.id);
  }
  return out;
}

/** Test seam: drop the memo when config is swapped underneath. */
export function resetMaterialValues() { valueCache = null; }
